// @flow
import api from '../api/sellerCatalogs';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

export const LOAD_SELLER_ENDED_CATALOGS_FAIL = 'la/domain/sellerEndedCatalogs/LOAD_FAIL';
export const LOAD_SELLER_ENDED_CATALOGS_REQUEST = 'la/domain/sellerEndedCatalogs/LOAD_REQUEST';
export const LOAD_SELLER_ENDED_CATALOGS_SUCCESS = 'la/domain/sellerEndedCatalogs/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SELLER_ENDED_CATALOGS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.sellerId]),
            };
        case LOAD_SELLER_ENDED_CATALOGS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_SELLER_ENDED_CATALOGS_SUCCESS:
            const { actionTime, sellerId } = action.meta;
            const newState = {
                ...state,
                byId: {
                    ...state.byId,
                    [sellerId]: action.payload.catalogs.map(catalog => catalog.catalogId),
                },
                loading: difference(state.loading, [sellerId]),
            };
            // TODO: make this reducer pure
            // dont set loaded if we're on the server because we'll need to get the rest
            newState.loaded = global.__SERVER__ ? { ...state.loaded } : { ...state.loaded, [sellerId]: actionTime };
            return newState;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.sellerEndedCatalogs;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

// takes in a (state, id) and returns the data
export const getEndedCatalogs = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || []);

// takes in a (state, id) and returns the load time
export const getLoadTimeForEndedCatalogs = createSelector(
    [loadedSelector, idSelector],
    (loaded, id) => loaded[id] || 0
);

// takes in a (state, id) and returns the is loading
export const isEndedCatalogsLoading = createSelector([loadingSelector, idSelector], (loading, id) => {
    return loading.includes(id);
});

const shouldFetchEndedCatalogs = (state, sellerId) => {
    if (!sellerId) return false;
    const item = getEndedCatalogs(state, sellerId);
    if (item.length) {
        const loaded = getLoadTimeForEndedCatalogs(state, sellerId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    const loading = isEndedCatalogsLoading(state, sellerId);
    return !loading;
};

/* ACTION CREATORS */
const loadEndedCatalogs = (sellerId, page, count, sortTerm) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: sellerId,
            type: LOAD_SELLER_ENDED_CATALOGS_REQUEST,
        });
        const response = await api.fetchSellerEndedCatalogsById({
            authToken,
            count,
            deployment,
            page,
            sellerId,
            sortTerm,
        });
        dispatch({
            meta: { actionTime: Date.now(), sellerId },
            payload: response.data,
            type: LOAD_SELLER_ENDED_CATALOGS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { sellerId },
            payload: error,
            type: LOAD_SELLER_ENDED_CATALOGS_FAIL,
        });
    }
};

export const fetchSellerEndedCatalogsIfNeeded = (
    sellerId: number,
    page: number,
    count: number,
    sortTerm: string
) => async (dispatch: Function, getState: Function) => {
    if (shouldFetchEndedCatalogs(getState(), sellerId)) {
        return dispatch(loadEndedCatalogs(sellerId, page, count, sortTerm));
    }
    return Promise.resolve();
};
